import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button, Accordion, AccordionSummary,
  AccordionDetails, Grid, FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, RadioGroup, Radio, Alert, CircularProgress, Divider, IconButton,
  Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Chip, InputAdornment,
  Snackbar
} from '@mui/material';
import {
  ExpandMore, Add, Delete, Save, Send, Thermostat, PhotoCamera, Draw, CheckCircle,
  Person, Business, Settings, Opacity, Speed, ElectricBolt, TrendingUp, Air, Science, Visibility,
  Inventory, Info
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignatureCanvas from '../../components/SignatureCanvas';
import ImageUploader from '../../components/ImageUploader';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';
import { calcPowerSinglePhase, calcPowerThreePhase, calculateCOP } from '../../utils/electricalCalculations';

const API_URL = process.env.REACT_APP_API_URL || '';

const capitalizeWords = (text) => {
  if (!text) return '';
  return text.split(/(\s+|-)/g).map((part) => {
    if (part === ' ' || part === '-') return part;
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  }).join('');
};

export default function WPProtocolFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const customer = location.state?.customer;
  const editFormData = location.state?.formData;
  const templateData = location.state?.templateData;
  const editMode = location.state?.editMode;
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState('panel1');
  const [show3Phase, setShow3Phase] = useState(false);
  
  // Signature Dialogs
  const [monteurSigDialog, setMonteurSigDialog] = useState(false);
  const [kundeSigDialog, setKundeSigDialog] = useState(false);
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Auto-Save States
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [autoSavedData, setAutoSavedData] = useState(null);
  const autoSaveTimerRef = React.useRef(null);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  // Determine data source ZUERST
  const sourceData = templateData || editFormData;

  // Auto-Focus Funktion
  const handleAccordionChange = (panel) => {
    setExpanded(expanded === panel ? '' : panel);
    
    // Focus auf erstes Input-Feld nach kurzer Verzögerung (für Animation)
    setTimeout(() => {
      if (expanded !== panel) {
        const accordionElement = document.querySelector(`[data-panel="${panel}"]`);
        if (accordionElement) {
          const firstInput = accordionElement.querySelector('input, textarea, select');
          if (firstInput) {
            firstInput.focus();
          }
        }
      }
    }, 300);
  };

  // Checkbox Keyboard Handler (Tab + Enter)
  const handleCheckboxKeyDown = (e, currentValue, onChange) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onChange(!currentValue);
    }
  };

  // Form Data
  const [formData, setFormData] = useState({
    // Protocol ID (für Updates)
    protocol_id: sourceData?.protocol_id || sourceData?._id || null,
    
    // Allgemeine Informationen
    datum: sourceData?.datum || new Date().toISOString().split('T')[0],
    auftragsnummer: sourceData?.auftragsnummer || '',
    kunde_name: customer?.name || sourceData?.kunde_name || '',
    ku_nr: customer?.ku_nr || sourceData?.ku_nr || '',
    kunde_email: customer?.email || sourceData?.kunde_email || '',
    standort: sourceData?.standort || '',
    
    // Geräte-Typ
    ist_monoblock: sourceData?.ist_monoblock || false,
    
    // Hersteller & Heizleistung
    hersteller: sourceData?.hersteller || 'Panasonic',
    hersteller_sonstiger: sourceData?.hersteller_sonstiger || '',
    nenn_heizleistung: sourceData?.nenn_heizleistung || '',
    
    // Innengerät (nur wenn nicht Monoblock)
    innengeraet: sourceData?.innengeraet || {
      montageort: '',
      modell: '',
      seriennummer: '',
      vorlauftemperatur: '',
      ruecklauftemperatur: '',
      wasserdurchfluss_m3h: '',
      wasserdurchfluss_lmin: '',
      wasserdurchfluss_lh: '',
      warmwasserspeicher_tw: '',
      arbeiten: {
        wasserdruck_pruefen: false,
        wasserfilter_reinigen: false,
        schlammabscheider_reinigen: false,
        umwaelzpumpen_pruefen: false,
        elektrische_anschluesse_pruefen: false,
        automatikentluefter_pruefen: false,
        ueberstroemventil_pruefen: false,
        drei_wege_ventile_pruefen: false,
        innengeraet_reinigen: false,
        steuerung_pruefen: false
      }
    },
    
    // Außengerät
    aussengeraet: sourceData?.aussengeraet || {
      montageort: '',
      modell: '',
      seriennummer: '',
      aussentemperatur: '',
      stromaufnahme: '',
      spannung_phase1: '',
      spannung_phase2: '',
      spannung_phase3: '',
      arbeiten: {
        waermetauscher_reinigen: false,
        kaeltetechnische_anschluesse_pruefen: false,
        kondensatwanne_reinigen: false,
        innenraum_reinigen: false,
        lueftermotor_pruefen: false,
        isolierung_pruefen: false,
        elektrische_anschluesse_pruefen: false,
        sicherheitseinrichtungen_testen: false,
        kompressor_pruefen: false,
        kaeltemitteldruecke_pruefen: false,
        stromaufnahme_messen: false
      }
    },
    
    // Monoblock-Messwerte
    monoblock_vorlauftemperatur: sourceData?.monoblock_vorlauftemperatur || '',
    monoblock_ruecklauftemperatur: sourceData?.monoblock_ruecklauftemperatur || '',
    monoblock_wasserdurchfluss_m3h: sourceData?.monoblock_wasserdurchfluss_m3h || '',
    monoblock_wasserdurchfluss_lmin: sourceData?.monoblock_wasserdurchfluss_lmin || '',
    monoblock_wasserdurchfluss_lh: sourceData?.monoblock_wasserdurchfluss_lh || '',
    monoblock_warmwasserspeicher_tw: sourceData?.monoblock_warmwasserspeicher_tw || '',
    monoblock_arbeiten: sourceData?.monoblock_arbeiten || {
      wasserdruck_pruefen: false,
      wasserfilter_reinigen: false,
      schlammabscheider_reinigen: false,
      umwaelzpumpen_pruefen: false,
      elektrische_anschluesse_pruefen: false,
      automatikentluefter_pruefen: false,
      ueberstroemventil_pruefen: false,
      drei_wege_ventile_pruefen: false,
      innengeraet_reinigen: false,
      steuerung_pruefen: false
    },
    
    // Dichteprüfung & Kältemittel
    dichtepr_notwendig: sourceData?.dichtepr_notwendig || 'nein',
    dichtepr_durchgefuehrt: sourceData?.dichtepr_durchgefuehrt || 'nein',
    pruefintervall: sourceData?.pruefintervall || '12',
    anlage_status: sourceData?.anlage_status || 'dicht',
    kaeltemittel: sourceData?.kaeltemittel || 'R410a',
    kaeltemittel_sonstiges: sourceData?.kaeltemittel_sonstiges || '',
    fuellmenge_typenschild: sourceData?.fuellmenge_typenschild || '',
    nachfuellmenge: sourceData?.nachfuellmenge || '0',
    gesamtfuellmenge: sourceData?.gesamtfuellmenge || '',
    
    // Bilder
    bilder: sourceData?.bilder || [],
    
    // Hinweise & Kundenzufriedenheit
    hinweise: sourceData?.hinweise || '',
    kunde_zufrieden: sourceData?.kunde_zufrieden || 'ja',
    kunde_zufrieden_begruendung: sourceData?.kunde_zufrieden_begruendung || '',
    kunde_name_unterschrift: sourceData?.kunde_name_unterschrift || '',
    
    // Unterschriften
    monteur_unterschrift: sourceData?.monteur_unterschrift || '',
    kunde_unterschrift: sourceData?.kunde_unterschrift || '',
    
    monteur: sourceData?.monteur || user?.full_name || user?.username || '',
    status: sourceData?.status || 'draft'
  });

  // Berechnete Werte State
  const [berechnungen, setBerechnungen] = useState({
    heizleistung: null,
    effizienz: null,
    status: null,
    empfehlung: null,
    elektrischeLeistung: null,
    elektrischeLeistungKW: null,
    cop: null,
    copEffizienz: null,
    ist3Phase: false
  });

  // Auto-Berechnung bei Änderung der Messwerte
  useEffect(() => {
    berechneHeizleistung();
  }, [
    formData.ist_monoblock,
    formData.monoblock_vorlauftemperatur,
    formData.monoblock_ruecklauftemperatur,
    formData.monoblock_wasserdurchfluss_m3h,
    formData.innengeraet.vorlauftemperatur,
    formData.innengeraet.ruecklauftemperatur,
    formData.innengeraet.wasserdurchfluss_m3h,
    formData.nenn_heizleistung,
    formData.aussengeraet.spannung_phase1,
    formData.aussengeraet.spannung_phase2,
    formData.aussengeraet.spannung_phase3,
    formData.aussengeraet.stromaufnahme,
    formData.aussengeraet.stromaufnahme_phase2,
    formData.aussengeraet.stromaufnahme_phase3
  ]);

  // Auto-Berechnung Gesamtfüllmenge
  useEffect(() => {
    const typenschild = parseFloat(formData.fuellmenge_typenschild);
    const nachfuell = parseFloat(formData.nachfuellmenge);
    
    if (!isNaN(typenschild) && !isNaN(nachfuell)) {
      const gesamt = typenschild + nachfuell;
      if (gesamt !== parseFloat(formData.gesamtfuellmenge)) {
        setFormData(prev => ({
          ...prev,
          gesamtfuellmenge: gesamt.toFixed(2)
        }));
      }
    }
  }, [formData.fuellmenge_typenschild, formData.nachfuellmenge]);

  const berechneHeizleistung = () => {
    try {
      const nennLeistung = parseFloat(formData.nenn_heizleistung);
      if (!nennLeistung) return;

      let vorlauf, ruecklauf, durchfluss;
      
      if (formData.ist_monoblock) {
        vorlauf = parseFloat(formData.monoblock_vorlauftemperatur);
        ruecklauf = parseFloat(formData.monoblock_ruecklauftemperatur);
        durchfluss = parseFloat(formData.monoblock_wasserdurchfluss_m3h);
      } else {
        vorlauf = parseFloat(formData.innengeraet.vorlauftemperatur);
        ruecklauf = parseFloat(formData.innengeraet.ruecklauftemperatur);
        durchfluss = parseFloat(formData.innengeraet.wasserdurchfluss_m3h);
      }

      if (!vorlauf || !ruecklauf || !durchfluss) {
        setBerechnungen({ 
          heizleistung: null, 
          effizienz: null, 
          status: null, 
          empfehlung: null,
          elektrischeLeistung: null,
          elektrischeLeistungKW: null,
          cop: null,
          copEffizienz: null,
          ist3Phase: false
        });
        return;
      }

      const deltaT = vorlauf - ruecklauf;
      if (deltaT <= 0) {
        setBerechnungen({
          heizleistung: 0,
          effizienz: 0,
          status: 'schlecht',
          empfehlung: 'Temperaturdifferenz ungültig',
          elektrischeLeistung: null,
          elektrischeLeistungKW: null,
          cop: null,
          copEffizienz: null,
          ist3Phase: false
        });
        return;
      }

      // Heizleistung berechnen
      const volumenstromLS = durchfluss * 1000 / 3600;
      const heizleistung = 4.18 * volumenstromLS * deltaT;
      const effizienz = (heizleistung / nennLeistung) * 100;

      // Elektrische Leistung berechnen
      let elektrischeLeistungResult = { watts: 0, kilowatts: 0 };
      let ist3Phase = false;
      const I1 = parseFloat(formData.aussengeraet.stromaufnahme);
      const I2 = parseFloat(formData.aussengeraet.stromaufnahme_phase2);
      const I3 = parseFloat(formData.aussengeraet.stromaufnahme_phase3);
      const L1 = parseFloat(formData.aussengeraet.spannung_phase1);
      const L2 = parseFloat(formData.aussengeraet.spannung_phase2);
      const L3 = parseFloat(formData.aussengeraet.spannung_phase3);

      if (I1 && L1) {
        // Prüfe ob 3-Phasen (L2 und L3 vorhanden)
        if (L2 && L3) {
          ist3Phase = true;
          
          // Wenn alle 3 Stromaufnahmen vorhanden sind, berechne jede Phase einzeln
          if (I2 && I3) {
            const p1 = calcPowerSinglePhase(L1, I1);
            const p2 = calcPowerSinglePhase(L2, I2);
            const p3 = calcPowerSinglePhase(L3, I3);
            elektrischeLeistungResult = {
              watts: p1.watts + p2.watts + p3.watts,
              kilowatts: parseFloat(((p1.watts + p2.watts + p3.watts) / 1000).toFixed(3))
            };
          } else {
            // Fallback: Symmetrische Last mit nur einem Strom
            const avgVoltage = (L1 + L2 + L3) / 3;
            elektrischeLeistungResult = calcPowerThreePhase(avgVoltage, I1);
          }
        } else {
          // 1-Phase System
          elektrischeLeistungResult = calcPowerSinglePhase(L1, I1);
        }
      }

      // COP berechnen
      let copResult = { cop: 0, efficiency_percent: 0 };
      if (elektrischeLeistungResult.kilowatts > 0) {
        copResult = calculateCOP(heizleistung, elektrischeLeistungResult.kilowatts);
      }

      let status, empfehlung;
      if (effizienz >= 80) {
        status = 'gut';
        empfehlung = 'Anlage läuft im normalen Betriebsbereich';
      } else if (effizienz >= 60) {
        status = 'mittel';
        empfehlung = 'Anlage muss beobachtet werden';
      } else {
        status = 'schlecht';
        empfehlung = 'Anlage muss überprüft werden';
      }

      setBerechnungen({
        heizleistung: heizleistung.toFixed(2),
        effizienz: effizienz.toFixed(1),
        status,
        empfehlung,
        elektrischeLeistung: elektrischeLeistungResult.watts,
        elektrischeLeistungKW: elektrischeLeistungResult.kilowatts,
        cop: copResult.cop,
        copEffizienz: copResult.efficiency_percent,
        ist3Phase
      });
    } catch (e) {
      setBerechnungen({ 
        heizleistung: null, 
        effizienz: null, 
        status: null, 
        empfehlung: null,
        elektrischeLeistung: null,
        elektrischeLeistungKW: null,
        cop: null,
        copEffizienz: null,
        ist3Phase: false
      });
    }
  };

  // Wasserdurchfluss Auto-Umrechnung
  const handleWasserdurchflussChange = (field, value) => {
    // Erlaube leere Eingabe oder Eingabe mit Dezimalpunkt
    if (value === '' || value === '.' || value === '0.') {
      if (formData.ist_monoblock) {
        setFormData({
          ...formData,
          monoblock_wasserdurchfluss_m3h: field === 'm3h' ? value : '',
          monoblock_wasserdurchfluss_lmin: field === 'lmin' ? value : '',
          monoblock_wasserdurchfluss_lh: field === 'lh' ? value : ''
        });
      } else {
        setFormData({
          ...formData,
          innengeraet: {
            ...formData.innengeraet,
            wasserdurchfluss_m3h: field === 'm3h' ? value : '',
            wasserdurchfluss_lmin: field === 'lmin' ? value : '',
            wasserdurchfluss_lh: field === 'lh' ? value : ''
          }
        });
      }
      return;
    }

    const val = parseFloat(value);
    if (isNaN(val) || val < 0) {
      return; // Ignoriere ungültige Eingaben
    }

    let m3h, lmin, lh;
    if (field === 'm3h') {
      m3h = value; // Behalte Roheingabe
      lmin = (val * 1000 / 60).toFixed(2);
      lh = (val * 1000).toFixed(2);
    } else if (field === 'lmin') {
      lmin = value; // Behalte Roheingabe
      m3h = (val * 60 / 1000).toFixed(3);
      lh = (val * 60).toFixed(2);
    } else if (field === 'lh') {
      lh = value; // Behalte Roheingabe
      m3h = (val / 1000).toFixed(3);
      lmin = (val / 60).toFixed(2);
    }

    if (formData.ist_monoblock) {
      setFormData({
        ...formData,
        monoblock_wasserdurchfluss_m3h: m3h,
        monoblock_wasserdurchfluss_lmin: lmin,
        monoblock_wasserdurchfluss_lh: lh
      });
    } else {
      setFormData({
        ...formData,
        innengeraet: {
          ...formData.innengeraet,
          wasserdurchfluss_m3h: m3h,
          wasserdurchfluss_lmin: lmin,
          wasserdurchfluss_lh: lh
        }
      });
    }
  };

  // Standard-Wasserdurchfluss-Werte laden
  const handleLoadStandardWasserdurchfluss = async () => {
    if (!formData.nenn_heizleistung) {
      alert('Bitte zuerst Nenn-Heizleistung eingeben');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/wp-protocols/standard-wasserdurchfluss/${formData.nenn_heizleistung}`
      );

      if (response.data.success) {
        const { m3h, lmin, lh } = response.data.wasserdurchfluss;
        
        if (formData.ist_monoblock) {
          setFormData({
            ...formData,
            monoblock_wasserdurchfluss_m3h: m3h,
            monoblock_wasserdurchfluss_lmin: lmin,
            monoblock_wasserdurchfluss_lh: lh
          });
        } else {
          setFormData({
            ...formData,
            innengeraet: {
              ...formData.innengeraet,
              wasserdurchfluss_m3h: m3h,
              wasserdurchfluss_lmin: lmin,
              wasserdurchfluss_lh: lh
            }
          });
        }
        
        // Snackbar für Erfolg
        setSnackbar({
          open: true,
          message: `Standard-Werte für ${formData.nenn_heizleistung} kW geladen: ${m3h} m³/h (${lmin} L/min)`,
          severity: 'success'
        });
      } else {
        alert('Fehler beim Laden der Standard-Werte');
      }
    } catch (error) {
      console.error('Error loading standard wasserdurchfluss:', error);
      alert('Fehler beim Laden der Standard-Werte');
    }
  };

  // Format Auftragsnummer: XXXX/YY (z.B. 0001/26, 0028/26, 0321/26, 1234/26)
  // Formatierung erfolgt beim Verlassen des Feldes (onBlur)
  const handleAuftragsnummerChange = (value) => {
    // Entferne alles außer Zahlen und /
    const cleaned = value.replace(/[^\d/]/g, '');
    setFormData(prev => ({ ...prev, auftragsnummer: cleaned }));
  };

  // Formatierung beim Verlassen des Feldes
  const handleAuftragsnummerBlur = () => {
    const value = formData.auftragsnummer;
    // Extrahiere nur Zahlen
    const numbers = value.replace(/[^\d]/g, '');
    
    if (numbers.length === 0) {
      return;
    }
    
    // Begrenze auf maximal 4 Ziffern
    const limitedNumbers = numbers.slice(0, 4);
    
    // Formatierung: Immer 4 Stellen mit führenden Nullen
    // 1 → 0001/26, 28 → 0028/26, 321 → 0321/26, 1234 → 1234/26
    if (limitedNumbers.length >= 1) {
      const currentYear = new Date().getFullYear().toString().slice(-2);
      // Fülle mit führenden Nullen auf 4 Stellen auf
      const paddedNumbers = limitedNumbers.padStart(4, '0');
      const formatted = `${paddedNumbers}/${currentYear}`;
      setFormData(prev => ({ ...prev, auftragsnummer: formatted }));
    }
  };

  // ============================================
  // AUTO-SAVE FUNKTIONEN
  // ============================================
  const AUTO_SAVE_KEY = editMode && editFormData?._id 
    ? `wp_protocol_autosave_edit_${editFormData._id}`
    : 'wp_protocol_autosave';
  const AUTO_SAVE_EXPIRY = 24 * 60 * 60 * 1000; // 24 Stunden

  // Beim Laden: Prüfe ob Auto-Save vorhanden
  useEffect(() => {
    const savedData = localStorage.getItem(AUTO_SAVE_KEY);
    const TEMPLATE_SESSION_KEY = 'wp_template_session';
    
    // Template-Modus: Markiere in SessionStorage (überlebt Re-Mounts in gleicher Session)
    if (templateData) {
      const existingSession = sessionStorage.getItem(TEMPLATE_SESSION_KEY);
      
      if (!existingSession) {
        // Erstes Mal in dieser Session - Markiere als Template-Session
        sessionStorage.setItem(TEMPLATE_SESSION_KEY, Date.now().toString());
        
        if (savedData) {
          localStorage.removeItem(AUTO_SAVE_KEY);
          console.log('🔄 WP Auto-Save: Template geladen, altes Auto-Save gelöscht');
        } else {
          console.log('📋 WP Template-Modus: Kein altes Auto-Save vorhanden');
        }
      } else {
        console.log('📋 WP Template-Session läuft bereits, Auto-Save erlaubt');
      }
      return;
    }

    // Kein Template-Modus: Lösche Template-Session-Marker
    sessionStorage.removeItem(TEMPLATE_SESSION_KEY);

    // Prüfe Auto-Save
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const now = Date.now();
        const age = now - parsed.timestamp;

        // Prüfe ob < 24h alt
        if (age < AUTO_SAVE_EXPIRY) {
          setAutoSavedData(parsed.data);
          setShowRestoreDialog(true);
        } else {
          // Zu alt, lösche Auto-Save
          localStorage.removeItem(AUTO_SAVE_KEY);
        }
      } catch (e) {
        console.error('❌ WP Auto-Save konnte nicht geladen werden:', e);
        localStorage.removeItem(AUTO_SAVE_KEY);
      }
    }
  }, [AUTO_SAVE_KEY, templateData]);

  // Auto-Save: Speichere Formulardaten (debounced)
  useEffect(() => {
    // Debounce: Warte 3 Sekunden nach letzter Änderung
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    setAutoSaveStatus('Wird gespeichert...');

    autoSaveTimerRef.current = setTimeout(() => {
      // Nur speichern wenn Formulardaten vorhanden
      const hasContent = formData.kunde_name || formData.auftragsnummer || formData.nenn_heizleistung;
      
      const modus = editMode && editFormData?._id ? `Edit (ID: ${editFormData._id})` : 'Neu';
      console.log(`📋 WP Auto-Save Check [${modus}]:`, {
        key: AUTO_SAVE_KEY,
        kunde_name: formData.kunde_name,
        auftragsnummer: formData.auftragsnummer,
        nenn_heizleistung: formData.nenn_heizleistung,
        hasContent
      });

      if (hasContent) {
        try {
          const dataToSave = {
            data: formData,
            timestamp: Date.now()
          };
          localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(dataToSave));
          const now = new Date();
          const timeStr = now.toLocaleTimeString('de-DE');
          setLastAutoSaveTime(timeStr);
          setAutoSaveStatus(`Gespeichert um ${timeStr}`);
          console.log(`💾 WP Auto-Save gespeichert [${modus}] um ${timeStr} → Key: ${AUTO_SAVE_KEY}`);
        } catch (e) {
          console.error('❌ WP Auto-Save Fehler:', e);
          setAutoSaveStatus('Fehler beim Speichern');
        }
      } else {
        console.log('⏭️ WP Auto-Save: Keine Daten zum Speichern');
        setAutoSaveStatus('');
      }
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, AUTO_SAVE_KEY, editMode, editFormData]);

  // Lösche Auto-Save nach erfolgreichem Submit
  const clearAutoSave = () => {
    localStorage.removeItem(AUTO_SAVE_KEY);
    console.log('🗑️ WP Auto-Save gelöscht');
  };

  // Stelle Auto-Save wieder her
  const handleRestoreAutoSave = () => {
    if (autoSavedData) {
      setFormData(autoSavedData);
      setShowRestoreDialog(false);
      setSnackbar({
        open: true,
        message: 'Ihr begonnenes Formular wurde wiederhergestellt!',
        severity: 'success'
      });
    }
  };

  // Verwerfe Auto-Save
  const handleDiscardAutoSave = () => {
    clearAutoSave();
    setShowRestoreDialog(false);
    setAutoSavedData(null);
  };

  const handleSubmit = async (sendEmail = false) => {
    // Validierung
    if (!formData.auftragsnummer) {
      setError('Auftragsnummer ist ein Pflichtfeld');
      return;
    }
    if (!formData.nenn_heizleistung) {
      setError('Nenn-Heizleistung ist ein Pflichtfeld');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const endpoint = sendEmail ? '/wp-protocols' : '/wp-protocols/save';
      console.log('🔧 Speichere WP-Protokoll:', { endpoint, sendEmail });
      const response = await axios.post(`${API_URL}${endpoint}`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Protokoll gespeichert:', response.data);
      if (response.data.success) {
        clearAutoSave(); // Lösche Auto-Save nach erfolgreichem Speichern
        navigate('/protocol/wp-protocols', {
          state: { message: sendEmail ? 'WP-Protokoll erfolgreich versendet' : 'WP-Protokoll als Entwurf gespeichert' }
        });
      } else {
        setError('Speichern fehlgeschlagen - Keine Erfolgsbestätigung vom Server');
      }
    } catch (err) {
      console.error('❌ Fehler beim Speichern:', err);
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/wp-protocols/preview`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success && response.data.pdf_id) {
        window.open(`${API_URL}/protocols/preview/${response.data.pdf_id}`, '_blank');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler bei PDF-Vorschau');
    } finally {
      setLoading(false);
    }
  };

  const getEffizienzColor = () => {
    if (berechnungen.status === 'gut') return 'success';
    if (berechnungen.status === 'mittel') return 'warning';
    if (berechnungen.status === 'schlecht') return 'error';
    return 'default';
  };

  return (
    <>
      <AppHeader title="Wartungsprotokoll Wärmepumpe" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
            <Air sx={{ mr: 1, verticalAlign: 'middle' }} />
            Wartungsprotokoll Wärmepumpe
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Auto-Save Status Indikator */}
          {autoSaveStatus && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                py: 0.5,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
              icon={<span>💾</span>}
            >
              <strong>Auto-Speicherung{editMode ? ' (Entwurf)' : ''}:</strong> {autoSaveStatus}
            </Alert>
          )}

          {/* Allgemeine Informationen */}
          <Accordion expanded={expanded === 'panel1'} onChange={() => handleAccordionChange('panel1')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography><Business sx={{ mr: 1, verticalAlign: 'middle' }} />Allgemeine Informationen</Typography>
            </AccordionSummary>
            <AccordionDetails data-panel="panel1">
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Datum"
                    type="date"
                    value={formData.datum}
                    onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    label="Auftragsnummer"
                    value={formData.auftragsnummer}
                    onChange={(e) => handleAuftragsnummerChange(e.target.value)}
                    onBlur={handleAuftragsnummerBlur}
                    placeholder="z.B. 321"
                    sx={{
                      width: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFE5B4',
                        '&:hover': { backgroundColor: '#FFD699' },
                        '&.Mui-focused': { backgroundColor: '#FFEDCC' }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Kundenname"
                    value={formData.kunde_name || ''}
                    onChange={(e) => setFormData({ ...formData, kunde_name: capitalizeWords(e.target.value) })}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Standort"
                    value={formData.standort || ''}
                    onChange={(e) => setFormData({ ...formData, standort: capitalizeWords(e.target.value) })}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Monteur/Techniker"
                    value={formData.monteur || ''}
                    onChange={(e) => setFormData({ ...formData, monteur: capitalizeWords(e.target.value) })}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Divider />
              </Box>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Kundennummer"
                    value={formData.ku_nr || ''}
                    disabled
                    sx={{ 
                      minWidth: 200,
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        cursor: 'not-allowed'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Kunden-Email"
                    type="email"
                    value={formData.kunde_email || ''}
                    onChange={(e) => setFormData({ ...formData, kunde_email: e.target.value })}
                    sx={{ minWidth: 300 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Geräte-Informationen */}
          <Accordion expanded={expanded === 'panel2'} onChange={() => handleAccordionChange('panel2')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography><Settings sx={{ mr: 1, verticalAlign: 'middle' }} />Geräte-Informationen</Typography>
            </AccordionSummary>
            <AccordionDetails data-panel="panel2">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.ist_monoblock}
                        onChange={(e) => setFormData({ ...formData, ist_monoblock: e.target.checked })}
                        onKeyDown={(e) => handleCheckboxKeyDown(e, formData.ist_monoblock, (val) => setFormData({ ...formData, ist_monoblock: val }))}
                        tabIndex={0}
                      />
                    }
                    label="Monoblock (nur Außengerät, kein separates Innengerät)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Hersteller</InputLabel>
                    <Select
                      value={formData.hersteller || ''}
                      label="Hersteller"
                      onChange={(e) => setFormData({ ...formData, hersteller: e.target.value })}
                    >
                      {['Panasonic', 'Kaysun', 'Daikin', 'Mitsubishi', 'Rotex', 'Fujitsu', 'Sonstiger'].map(h => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.hersteller === 'Sonstiger' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hersteller (Sonstiger)"
                      value={formData.hersteller_sonstiger || ''}
                      onChange={(e) => setFormData({ ...formData, hersteller_sonstiger: capitalizeWords(e.target.value) })}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Nenn-Heizleistung (kW)"
                    type="number"
                    value={formData.nenn_heizleistung || ''}
                    onChange={(e) => setFormData({ ...formData, nenn_heizleistung: e.target.value })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kW</InputAdornment>
                    }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Innengerät/Monoblock Messwerte */}
          {!formData.ist_monoblock && (
            <Accordion expanded={expanded === 'panel3'} onChange={() => handleAccordionChange('panel3')}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography><Thermostat sx={{ mr: 1, verticalAlign: 'middle' }} />Innengerät</Typography>
              </AccordionSummary>
              <AccordionDetails data-panel="panel3">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Montageort"
                      value={formData.innengeraet.montageort || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, montageort: capitalizeWords(e.target.value) }
                      })}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Modellnummer"
                      value={formData.innengeraet.modell || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, modell: e.target.value.toUpperCase() }
                      })}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Seriennummer"
                      value={formData.innengeraet.seriennummer || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, seriennummer: e.target.value.toUpperCase() }
                      })}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ my: 3 }}>
                  <Divider><Typography variant="body2" color="text.secondary">Messwerte</Typography></Divider>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Vorlauftemperatur"
                      type="number"
                      value={formData.innengeraet.vorlauftemperatur || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, vorlauftemperatur: e.target.value }
                      })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Rücklauftemperatur"
                      type="number"
                      value={formData.innengeraet.ruecklauftemperatur || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, ruecklauftemperatur: e.target.value }
                      })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Warmwasserspeicher TW"
                      type="number"
                      value={formData.innengeraet.warmwasserspeicher_tw || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        innengeraet: { ...formData.innengeraet, warmwasserspeicher_tw: e.target.value }
                      })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ my: 3 }}>
                  <Divider><Typography variant="body2" color="text.secondary">Wasserdurchfluss</Typography></Divider>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleLoadStandardWasserdurchfluss}
                    startIcon={<Opacity />}
                  >
                    Standard-Werte für {formData.nenn_heizleistung || 'X'} kW laden
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (m³/h)"
                      type="number"
                      value={formData.innengeraet.wasserdurchfluss_m3h || ''}
                      onChange={(e) => handleWasserdurchflussChange('m3h', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">m³/h</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (L/min)"
                      type="number"
                      value={formData.innengeraet.wasserdurchfluss_lmin || ''}
                      onChange={(e) => handleWasserdurchflussChange('lmin', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">L/min</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (L/h)"
                      type="number"
                      value={formData.innengeraet.wasserdurchfluss_lh || ''}
                      onChange={(e) => handleWasserdurchflussChange('lh', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">L/h</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  {berechnungen.heizleistung && (
                    <Grid item xs={12}>
                      <Card sx={{ bgcolor: '#f5f5f5' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Berechnete Werte
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">Heizleistung</Typography>
                              <Typography variant="h6">{berechnungen.heizleistung} kW</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">Effizienz</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">{berechnungen.effizienz}%</Typography>
                                <Chip
                                  label={berechnungen.status}
                                  color={getEffizienzColor()}
                                  size="small"
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Alert severity={getEffizienzColor()}>{berechnungen.empfehlung}</Alert>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}><Divider><Typography variant="body2">Durchgeführte Arbeiten</Typography></Divider></Grid>

                  {[
                    { key: 'wasserdruck_pruefen', label: 'Wasserdruck prüfen ggf. nachfüllen' },
                    { key: 'wasserfilter_reinigen', label: 'Wasserfilter im Rücklauf reinigen' },
                    { key: 'schlammabscheider_reinigen', label: 'Schlammabscheider reinigen (falls vorhanden)' },
                    { key: 'umwaelzpumpen_pruefen', label: 'Umwälzpumpen prüfen (Funktion/Laufgeräusche)' },
                    { key: 'elektrische_anschluesse_pruefen', label: 'Elektrische Anschlüsse und Isolierungen prüfen' },
                    { key: 'automatikentluefter_pruefen', label: 'Automatikentlüfter prüfen' },
                    { key: 'ueberstroemventil_pruefen', label: 'Überströmventil prüfen' },
                    { key: 'drei_wege_ventile_pruefen', label: '3-Wege Ventile prüfen' },
                    { key: 'innengeraet_reinigen', label: 'Innengerät prüfen und ggf. reinigen' },
                    { key: 'steuerung_pruefen', label: 'Steuerung/Programmierung prüfen ggf. anpassen' }
                  ].map(arbeit => (
                    <Grid item xs={12} sm={6} key={arbeit.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.innengeraet.arbeiten[arbeit.key]}
                            onChange={(e) => setFormData({
                              ...formData,
                              innengeraet: {
                                ...formData.innengeraet,
                                arbeiten: {
                                  ...formData.innengeraet.arbeiten,
                                  [arbeit.key]: e.target.checked
                                }
                              }
                            })}
                            onKeyDown={(e) => handleCheckboxKeyDown(
                              e,
                              formData.innengeraet.arbeiten[arbeit.key],
                              (val) => setFormData({
                                ...formData,
                                innengeraet: {
                                  ...formData.innengeraet,
                                  arbeiten: {
                                    ...formData.innengeraet.arbeiten,
                                    [arbeit.key]: val
                                  }
                                }
                              })
                            )}
                            tabIndex={0}
                          />
                        }
                        label={arbeit.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Monoblock Messwerte */}
          {formData.ist_monoblock && (
            <Accordion expanded={expanded === 'panel3m'} onChange={() => handleAccordionChange('panel3m')}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography><Thermostat sx={{ mr: 1, verticalAlign: 'middle' }} />Monoblock Messwerte</Typography>
              </AccordionSummary>
              <AccordionDetails data-panel="panel3">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Vorlauftemperatur"
                      type="number"
                      value={formData.monoblock_vorlauftemperatur || ''}
                      onChange={(e) => setFormData({ ...formData, monoblock_vorlauftemperatur: e.target.value })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Rücklauftemperatur"
                      type="number"
                      value={formData.monoblock_ruecklauftemperatur || ''}
                      onChange={(e) => setFormData({ ...formData, monoblock_ruecklauftemperatur: e.target.value })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Warmwasserspeicher TW"
                      type="number"
                      value={formData.monoblock_warmwasserspeicher_tw || ''}
                      onChange={(e) => setFormData({ ...formData, monoblock_warmwasserspeicher_tw: e.target.value })}
                      InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ my: 3 }}>
                  <Divider><Typography variant="body2" color="text.secondary">Wasserdurchfluss</Typography></Divider>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleLoadStandardWasserdurchfluss}
                    startIcon={<Opacity />}
                  >
                    Standard-Werte für {formData.nenn_heizleistung || 'X'} kW laden
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (m³/h)"
                      type="number"
                      value={formData.monoblock_wasserdurchfluss_m3h || ''}
                      onChange={(e) => handleWasserdurchflussChange('m3h', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">m³/h</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (L/min)"
                      type="number"
                      value={formData.monoblock_wasserdurchfluss_lmin || ''}
                      onChange={(e) => handleWasserdurchflussChange('lmin', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">L/min</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Wasserdurchfluss (L/h)"
                      type="number"
                      value={formData.monoblock_wasserdurchfluss_lh}
                      onChange={(e) => handleWasserdurchflussChange('lh', e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">L/h</InputAdornment> }}
                    />
                  </Grid>

                  {berechnungen.heizleistung && (
                    <Grid item xs={12}>
                      <Card sx={{ bgcolor: '#f5f5f5' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Berechnete Werte
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">Heizleistung</Typography>
                              <Typography variant="h6">{berechnungen.heizleistung} kW</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">Effizienz</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">{berechnungen.effizienz}%</Typography>
                                <Chip label={berechnungen.status} color={getEffizienzColor()} size="small" />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Alert severity={getEffizienzColor()}>{berechnungen.empfehlung}</Alert>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}><Divider><Typography variant="body2">Durchgeführte Arbeiten</Typography></Divider></Grid>

                  {[
                    { key: 'wasserdruck_pruefen', label: 'Wasserdruck prüfen ggf. nachfüllen' },
                    { key: 'wasserfilter_reinigen', label: 'Wasserfilter im Rücklauf reinigen' },
                    { key: 'schlammabscheider_reinigen', label: 'Schlammabscheider reinigen (falls vorhanden)' },
                    { key: 'umwaelzpumpen_pruefen', label: 'Umwälzpumpen prüfen (Funktion/Laufgeräusche)' },
                    { key: 'elektrische_anschluesse_pruefen', label: 'Elektrische Anschlüsse und Isolierungen prüfen' },
                    { key: 'automatikentluefter_pruefen', label: 'Automatikentlüfter prüfen' },
                    { key: 'ueberstroemventil_pruefen', label: 'Überströmventil prüfen' },
                    { key: 'drei_wege_ventile_pruefen', label: '3-Wege Ventile prüfen' },
                    { key: 'innengeraet_reinigen', label: 'Innengerät prüfen und ggf. reinigen' },
                    { key: 'steuerung_pruefen', label: 'Steuerung/Programmierung prüfen ggf. anpassen' }
                  ].map(arbeit => (
                    <Grid item xs={12} sm={6} key={arbeit.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.monoblock_arbeiten[arbeit.key]}
                            onChange={(e) => setFormData({
                              ...formData,
                              monoblock_arbeiten: {
                                ...formData.monoblock_arbeiten,
                                [arbeit.key]: e.target.checked
                              }
                            })}
                          />
                        }
                        label={arbeit.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Außengerät */}
          <Accordion expanded={expanded === 'panel4'} onChange={() => handleAccordionChange('panel4')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography><ElectricBolt sx={{ mr: 1, verticalAlign: 'middle' }} />Außengerät {formData.ist_monoblock && '(Monoblock)'}</Typography>
            </AccordionSummary>
            <AccordionDetails data-panel="panel4">
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Montageort"
                    value={formData.aussengeraet.montageort || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, montageort: capitalizeWords(e.target.value) }
                    })}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Modellnummer"
                    value={formData.aussengeraet.modell || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, modell: e.target.value.toUpperCase() }
                    })}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Seriennummer"
                    value={formData.aussengeraet.seriennummer || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, seriennummer: e.target.value.toUpperCase() }
                    })}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ my: 3 }}>
                <Divider><Typography variant="body2" color="text.secondary">Messwerte</Typography></Divider>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Außentemperatur"
                    type="number"
                    value={formData.aussengeraet.aussentemperatur || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, aussentemperatur: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ my: 3 }}>
                <Divider><Typography variant="body2" color="text.secondary">Elektrische Messwerte (Phase 1)</Typography></Divider>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Spannung L1-N"
                    type="number"
                    value={formData.aussengeraet.spannung_phase1 || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, spannung_phase1: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">V</InputAdornment> }}
                    helperText="Standard 230V (1-Phase) oder 400V (3-Phase)"
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Stromaufnahme L1"
                    type="number"
                    value={formData.aussengeraet.stromaufnahme || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      aussengeraet: { ...formData.aussengeraet, stromaufnahme: e.target.value }
                    })}
                    InputProps={{ endAdornment: <InputAdornment position="end">A</InputAdornment> }}
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button
                    variant={show3Phase ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setShow3Phase(!show3Phase)}
                    sx={{ minWidth: 200, height: 56 }}
                  >
                    {show3Phase ? "1-Phasig" : "3-Phasig"}
                  </Button>
                </Grid>
              </Grid>

              {show3Phase && (
                <>
                  <Box sx={{ my: 3 }}>
                    <Divider><Typography variant="body2" color="text.secondary">Phase 2</Typography></Divider>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Spannung L2-N"
                        type="number"
                        value={formData.aussengeraet.spannung_phase2 || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          aussengeraet: { ...formData.aussengeraet, spannung_phase2: e.target.value }
                        })}
                        InputProps={{ endAdornment: <InputAdornment position="end">V</InputAdornment> }}
                        sx={{ 
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Stromaufnahme L2"
                        type="number"
                        value={formData.aussengeraet.stromaufnahme_phase2 || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          aussengeraet: { ...formData.aussengeraet, stromaufnahme_phase2: e.target.value }
                        })}
                        InputProps={{ endAdornment: <InputAdornment position="end">A</InputAdornment> }}
                        sx={{ 
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#e3f2fd'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ my: 3 }}>
                    <Divider><Typography variant="body2" color="text.secondary">Phase 3</Typography></Divider>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Spannung L3-N"
                        type="number"
                        value={formData.aussengeraet.spannung_phase3 || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          aussengeraet: { ...formData.aussengeraet, spannung_phase3: e.target.value }
                        })}
                        InputProps={{ endAdornment: <InputAdornment position="end">V</InputAdornment> }}
                        sx={{ 
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Stromaufnahme L3"
                        type="number"
                        value={formData.aussengeraet.stromaufnahme_phase3 || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          aussengeraet: { ...formData.aussengeraet, stromaufnahme_phase3: e.target.value }
                        })}
                        InputProps={{ endAdornment: <InputAdornment position="end">A</InputAdornment> }}
                        sx={{ 
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#e3f2fd'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Berechnete Leistungswerte */}
              {(berechnungen.elektrischeLeistungKW || berechnungen.cop) && (
                <>
                  <Box sx={{ my: 3 }}>
                    <Divider>
                      <Typography variant="body2" color="text.secondary">
                        Berechnete Leistungswerte (Näherungswerte)
                      </Typography>
                    </Divider>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Die folgenden Werte sind <strong>grobe Berechnungen</strong> basierend auf Standardwerten (cosφ=0.85, η=0.90). 
                    Sie dienen als Richtwerte für die Beurteilung der Anlagenleistung während der Wartung.
                  </Alert>

                  <Grid container spacing={3}>
                    {berechnungen.elektrischeLeistungKW && (
                      <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2, bgcolor: '#e3f2fd', border: '2px solid #2196f3' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            <ElectricBolt sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Elektrische Leistung (geschätzt)
                          </Typography>
                          <Typography variant="h4" color="primary">
                            ≈ {berechnungen.elektrischeLeistungKW} kW
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            ≈ {berechnungen.elektrischeLeistung} Watt
                          </Typography>
                          <Chip 
                            label={berechnungen.ist3Phase ? "3-Phasig (400V)" : "1-Phasig (230V)"} 
                            size="small" 
                            color={berechnungen.ist3Phase ? "warning" : "info"}
                            sx={{ mt: 1 }}
                          />
                        </Card>
                      </Grid>
                    )}

                    {berechnungen.cop > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          p: 2, 
                          bgcolor: berechnungen.cop >= 3.5 ? '#e8f5e9' : berechnungen.cop >= 2.5 ? '#fff3e0' : '#ffebee',
                          border: `2px solid ${berechnungen.cop >= 3.5 ? '#4caf50' : berechnungen.cop >= 2.5 ? '#ff9800' : '#f44336'}`
                        }}>
                          <Typography variant="subtitle2" sx={{ color: berechnungen.cop >= 3.5 ? '#4caf50' : berechnungen.cop >= 2.5 ? '#ff9800' : '#f44336' }} gutterBottom>
                            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                            COP - Coefficient of Performance (Richtwert)
                          </Typography>
                          <Typography variant="h4" sx={{ color: berechnungen.cop >= 3.5 ? '#4caf50' : berechnungen.cop >= 2.5 ? '#ff9800' : '#f44336' }}>
                            ≈ {berechnungen.cop}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Effizienz: ca. {berechnungen.copEffizienz}%
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                            {berechnungen.cop >= 3.5 && <Chip label="Sehr gut" size="small" color="success" />}
                            {berechnungen.cop >= 2.5 && berechnungen.cop < 3.5 && <Chip label="Gut" size="small" color="warning" />}
                            {berechnungen.cop < 2.5 && <Chip label="Prüfen erforderlich" size="small" color="error" />}
                          </Box>
                        </Card>
                      </Grid>
                    )}

                    {berechnungen.heizleistung && (
                      <Grid item xs={12}>
                        <Card sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            📊 <strong>Heizleistungs-Übersicht</strong> (Näherungswerte während Probelauf)
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">Heizleistung (berechnet)</Typography>
                              <Typography variant="h6">≈ {berechnungen.heizleistung} kW</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">Nenn-Heizleistung</Typography>
                              <Typography variant="h6">{formData.nenn_heizleistung} kW</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">Effizienz (grob)</Typography>
                              <Typography variant="h6">≈ {berechnungen.effizienz}%</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Chip 
                                label={berechnungen.status} 
                                color={berechnungen.status === 'gut' ? 'success' : berechnungen.status === 'mittel' ? 'warning' : 'error'}
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Grid>
                          </Grid>
                          {berechnungen.empfehlung && (
                            <Typography variant="body2" sx={{ mt: 2, p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                              💡 {berechnungen.empfehlung}
                            </Typography>
                          )}
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              <Box sx={{ my: 3 }}>
                <Divider><Typography variant="body2" color="text.secondary">Durchgeführte Arbeiten</Typography></Divider>
              </Box>

              <Grid container spacing={3}>

                {[
                  { key: 'waermetauscher_reinigen', label: 'Wärmetauscher am Außengerät prüfen und reinigen' },
                  { key: 'kaeltetechnische_anschluesse_pruefen', label: 'Kältetechnische Anschlüsse auf Dichtheit prüfen' },
                  { key: 'kondensatwanne_reinigen', label: 'Kondensatwanne und Ablauf am AG prüfen und reinigen' },
                  { key: 'innenraum_reinigen', label: 'Innenraum des Außengerätes prüfen und reinigen' },
                  { key: 'lueftermotor_pruefen', label: 'Lüftermotor auf Unwucht prüfen, Flügel ggf. reinigen' },
                  { key: 'isolierung_pruefen', label: 'Isolierung und Anschlusskabel auf Beschädigung prüfen' },
                  { key: 'elektrische_anschluesse_pruefen', label: 'Elektrische Anschlüsse und Klemmschrauben prüfen' },
                  { key: 'sicherheitseinrichtungen_testen', label: 'Sicherheitseinrichtungen (Druckschalter) testen' },
                  { key: 'kompressor_pruefen', label: 'Kompressor auf Unwucht/Geräusche prüfen' },
                  { key: 'kaeltemitteldruecke_pruefen', label: 'Kältemitteldrücke und Temperaturen prüfen' },
                  { key: 'stromaufnahme_messen', label: 'Stromaufnahme und Leistungsmessung am Außengerät' }
                ].map(arbeit => (
                  <Grid item xs={12} sm={6} key={arbeit.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.aussengeraet.arbeiten[arbeit.key]}
                          onChange={(e) => setFormData({
                            ...formData,
                            aussengeraet: {
                              ...formData.aussengeraet,
                              arbeiten: {
                                ...formData.aussengeraet.arbeiten,
                                [arbeit.key]: e.target.checked
                              }
                            }
                          })}
                          onKeyDown={(e) => handleCheckboxKeyDown(
                            e,
                            formData.aussengeraet.arbeiten[arbeit.key],
                            (val) => setFormData({
                              ...formData,
                              aussengeraet: {
                                ...formData.aussengeraet,
                                arbeiten: {
                                  ...formData.aussengeraet.arbeiten,
                                  [arbeit.key]: val
                                }
                              }
                            })
                          )}
                          tabIndex={0}
                        />
                      }
                      label={arbeit.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Dichteprüfung & Kältemittel */}
          <Accordion expanded={expanded === 'panel5'} onChange={() => handleAccordionChange('panel5')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography><Science sx={{ mr: 1, verticalAlign: 'middle' }} />Dichteprüfung & Kältemittel</Typography>
            </AccordionSummary>
            <AccordionDetails data-panel="panel5">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Dichteprüfung notwendig?</InputLabel>
                    <Select
                      value={formData.dichtepr_notwendig || ''}
                      label="Dichteprüfung notwendig?"
                      onChange={(e) => setFormData({ ...formData, dichtepr_notwendig: e.target.value })}
                    >
                      <MenuItem value="ja">Ja</MenuItem>
                      <MenuItem value="nein">Nein</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Prüfintervall</InputLabel>
                    <Select
                      value={formData.pruefintervall || ''}
                      label="Prüfintervall"
                      onChange={(e) => setFormData({ ...formData, pruefintervall: e.target.value })}
                    >
                      <MenuItem value="3">3 Monate</MenuItem>
                      <MenuItem value="6">6 Monate</MenuItem>
                      <MenuItem value="12">12 Monate</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.dichtepr_notwendig === 'ja' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ minWidth: 200 }}>
                        <InputLabel>Durchgeführt?</InputLabel>
                        <Select
                          value={formData.dichtepr_durchgefuehrt || ''}
                          label="Durchgeführt?"
                          onChange={(e) => setFormData({ ...formData, dichtepr_durchgefuehrt: e.target.value })}
                        >
                          <MenuItem value="ja">Ja</MenuItem>
                          <MenuItem value="nein">Nein</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ minWidth: 200 }}>
                        <InputLabel>Anlage Status</InputLabel>
                        <Select
                          value={formData.anlage_status || ''}
                          label="Anlage Status"
                          onChange={(e) => setFormData({ ...formData, anlage_status: e.target.value })}
                        >
                          <MenuItem value="dicht">Dicht</MenuItem>
                          <MenuItem value="undicht">Undicht</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ my: 3 }}>
                <Divider><Typography variant="body2" color="text.secondary">Kältemittel</Typography></Divider>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Kältemittel</InputLabel>
                    <Select
                      value={formData.kaeltemittel || ''}
                      label="Kältemittel"
                      onChange={(e) => setFormData({ ...formData, kaeltemittel: e.target.value })}
                    >
                      {['R410a', 'R32', 'R407c', 'R290', 'R134a', 'Sonstiges'].map(k => (
                        <MenuItem key={k} value={k}>{k}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.kaeltemittel === 'Sonstiges' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Kältemittel (Sonstiges)"
                      value={formData.kaeltemittel_sonstiges || ''}
                      onChange={(e) => setFormData({ ...formData, kaeltemittel_sonstiges: e.target.value.toUpperCase() })}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Füllmenge lt. Typenschild"
                    type="number"
                    value={formData.fuellmenge_typenschild || ''}
                    onChange={(e) => setFormData({ ...formData, fuellmenge_typenschild: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Nachfüllmenge"
                    type="number"
                    value={formData.nachfuellmenge || ''}
                    onChange={(e) => setFormData({ ...formData, nachfuellmenge: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Gesamtfüllmenge"
                    type="number"
                    value={formData.gesamtfuellmenge || ''}
                    onChange={(e) => setFormData({ ...formData, gesamtfuellmenge: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Bilder, Hinweise & Unterschriften */}
          <Accordion 
            expanded={expanded === 'panel6'} 
            onChange={() => handleAccordionChange('panel6')}
            sx={{ border: '2px solid #4caf50', borderRadius: 1 }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMore />}
              sx={{ backgroundColor: '#e8f5e9' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory color="success" />
                <Typography variant="h6">Bilder, Hinweise & Unterschriften</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails data-panel="panel6">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                
                {/* 1. HINWEISE */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Info color="info" />
                    Hinweise
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Zusätzliche Hinweise oder Bemerkungen"
                    value={formData.hinweise || ''}
                    onChange={(e) => setFormData({ ...formData, hinweise: e.target.value })}
                    placeholder="Z.B. besondere Vorkommnisse, Empfehlungen, durchgeführte Reparaturen..."
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fafafa',
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                </Box>
                
                <Divider />
                
                {/* 2. BILDER/FOTOS */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhotoCamera color="success" />
                    Fotos (max. 5 Bilder)
                  </Typography>
                  <ImageUploader
                    images={formData.bilder || []}
                    onChange={(images) => setFormData({ ...formData, bilder: images })}
                    maxImages={5}
                  />
                </Box>
                
                <Divider />
                
                {/* 3. UNTERSCHRIFT MONTEUR */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Draw color="primary" />
                    Unterschrift Monteur *
                  </Typography>
                  <Box sx={{ 
                    border: '2px dashed #2196f3', 
                    borderRadius: 2, 
                    p: 3, 
                    textAlign: 'center',
                    backgroundColor: formData.monteur_unterschrift ? '#e3f2fd' : '#fafafa',
                    minHeight: 150
                  }}>
                    {formData.monteur_unterschrift ? (
                      <Box>
                        <img 
                          src={formData.monteur_unterschrift} 
                          alt="Monteur Unterschrift" 
                          style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                        />
                        <Box sx={{ mt: 2 }}>
                          <Button 
                            variant="outlined" 
                            onClick={() => setMonteurSigDialog(true)}
                          >
                            Neu unterschreiben
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Button 
                        variant="contained" 
                        size="large"
                        startIcon={<Draw />}
                        onClick={() => setMonteurSigDialog(true)}
                      >
                        Jetzt unterschreiben
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Divider />
                
                {/* 4. KUNDENZUFRIEDENHEIT */}
                <Box>
                  <Card sx={{ p: 3, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CheckCircle color="success" />
                      Kundenzufriedenheit
                    </Typography>
                    
                    <Typography variant="body1" gutterBottom fontWeight="medium" sx={{ mb: 2 }}>
                      Wurde die Wartung zu Ihrer Zufriedenheit durchgeführt?
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.kunde_zufrieden === 'ja'}
                            onChange={() => {
                              setFormData({ 
                                ...formData, 
                                kunde_zufrieden: 'ja',
                                kunde_zufrieden_begruendung: ''
                              });
                            }}
                            onKeyDown={(e) => handleCheckboxKeyDown(
                              e,
                              formData.kunde_zufrieden === 'ja',
                              () => setFormData({ 
                                ...formData, 
                                kunde_zufrieden: 'ja',
                                kunde_zufrieden_begruendung: ''
                              })
                            )}
                            tabIndex={0}
                            sx={{
                              color: 'success.light',
                              '&.Mui-checked': { color: 'success.main' }
                            }}
                          />
                        }
                        label={<Typography variant="body1"><strong>Ja</strong>, ich bin zufrieden</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.kunde_zufrieden === 'nein'}
                            onChange={() => setFormData({ ...formData, kunde_zufrieden: 'nein' })}
                            onKeyDown={(e) => handleCheckboxKeyDown(
                              e,
                              formData.kunde_zufrieden === 'nein',
                              () => setFormData({ ...formData, kunde_zufrieden: 'nein' })
                            )}
                            tabIndex={0}
                            sx={{
                              color: 'error.light',
                              '&.Mui-checked': { color: 'error.main' }
                            }}
                          />
                        }
                        label={<Typography variant="body1"><strong>Nein</strong>, ich bin nicht zufrieden</Typography>}
                      />
                    </Box>
                    
                    {formData.kunde_zufrieden === 'nein' && (
                      <Box sx={{ mt: 3 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Begründung (Pflichtfeld)"
                          value={formData.kunde_zufrieden_begruendung || ''}
                          onChange={(e) => setFormData({ ...formData, kunde_zufrieden_begruendung: e.target.value })}
                          placeholder="Bitte geben Sie an, warum Sie nicht zufrieden sind..."
                          required
                          sx={{
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff3cd',
                              borderColor: '#ffc107'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Card>
                </Box>
                
                <Divider />
                
                {/* 5. NAME KUNDE */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Name des Unterzeichners (optional)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Name des Kunden (falls abweichend)"
                    value={formData.kunde_name_unterschrift || ''}
                    onChange={(e) => setFormData({ ...formData, kunde_name_unterschrift: capitalizeWords(e.target.value) })}
                    placeholder="Z.B. wenn eine andere Person unterschreibt"
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  />
                </Box>
                
                <Divider />
                
                {/* 6. UNTERSCHRIFT KUNDE */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Draw color="success" />
                    Unterschrift Kunde *
                  </Typography>
                  <Box sx={{ 
                    border: '2px dashed #4caf50', 
                    borderRadius: 2, 
                    p: 3, 
                    textAlign: 'center',
                    backgroundColor: formData.kunde_unterschrift ? '#e8f5e9' : '#fafafa',
                    minHeight: 150
                  }}>
                    {formData.kunde_unterschrift ? (
                      <Box>
                        <img 
                          src={formData.kunde_unterschrift} 
                          alt="Kunde Unterschrift" 
                          style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                        />
                        <Box sx={{ mt: 2 }}>
                          <Button 
                            variant="outlined"
                            color="success"
                            onClick={() => setKundeSigDialog(true)}
                          >
                            Neu unterschreiben
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Button 
                        variant="contained" 
                        color="success"
                        size="large"
                        startIcon={<Draw />}
                        onClick={() => setKundeSigDialog(true)}
                      >
                        Jetzt unterschreiben
                      </Button>
                    )}
                  </Box>
                </Box>
                
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={handlePreview}
              disabled={loading}
            >
              PDF Vorschau
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => handleSubmit(false)}
              disabled={loading}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
            >
              Als Entwurf speichern
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => handleSubmit(true)}
              disabled={loading}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
            >
              {loading ? <CircularProgress size={24} /> : 'Speichern & Versenden'}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Signature Dialogs */}
      {/* MONTEUR UNTERSCHRIFT POPUP */}
      <Dialog 
        open={monteurSigDialog} 
        onClose={() => {
          if (!formData.monteur_unterschrift) {
            alert('Bitte unterschreiben Sie oder brechen Sie ab');
            return;
          }
          setMonteurSigDialog(false);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Monteur Unterschrift</DialogTitle>
        <DialogContent sx={{ minHeight: 400, p: 3 }}>
          <SignatureCanvas
            value={formData.monteur_unterschrift}
            onChange={(signature) => {
              setFormData({ ...formData, monteur_unterschrift: signature });
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setFormData({ ...formData, monteur_unterschrift: '' });
              setMonteurSigDialog(false);
            }}
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (!formData.monteur_unterschrift) {
                alert('Bitte unterschreiben Sie erst!');
                return;
              }
              setMonteurSigDialog(false);
            }}
            disabled={!formData.monteur_unterschrift}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* KUNDE UNTERSCHRIFT POPUP */}
      <Dialog 
        open={kundeSigDialog} 
        onClose={() => {
          if (!formData.kunde_unterschrift) {
            alert('Bitte unterschreiben Sie oder brechen Sie ab');
            return;
          }
          setKundeSigDialog(false);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Kunde Unterschrift</DialogTitle>
        <DialogContent sx={{ minHeight: 400, p: 3 }}>
          <SignatureCanvas
            value={formData.kunde_unterschrift}
            onChange={(signature) => {
              setFormData({ ...formData, kunde_unterschrift: signature });
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setFormData({ ...formData, kunde_unterschrift: '' });
              setKundeSigDialog(false);
            }}
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button 
            variant="contained"
            color="success"
            onClick={() => {
              if (!formData.kunde_unterschrift) {
                alert('Bitte unterschreiben Sie erst!');
                return;
              }
              setKundeSigDialog(false);
            }}
            disabled={!formData.kunde_unterschrift}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Auto-Save Wiederherstellungs-Dialog */}
      <Dialog 
        open={showRestoreDialog} 
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#4caf50', color: 'white' }}>
          💾 Begonnenes WP-Formular gefunden
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Es wurde ein automatisch gespeichertes WP-Formular gefunden. Möchten Sie Ihre Arbeit fortsetzen?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Das Formular wurde automatisch gespeichert, als Sie das letzte Mal daran gearbeitet haben.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDiscardAutoSave}
            color="error"
            variant="outlined"
          >
            Verwerfen & neu beginnen
          </Button>
          <Button 
            onClick={handleRestoreAutoSave}
            color="success"
            variant="contained"
            autoFocus
          >
            Wiederherstellen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
